/**
 * Booking Slice for Wadakaroo
 *
 * Manages state for workshop bookings:
 * - Calendar views (day/week/month)
 * - CRUD operations (ad-hoc and service-profile paths)
 * - Drag-and-drop rescheduling
 * - Status management
 * - Vehicle received (with optional loan car handover details)
 * - Enum metadata (category types for Service Type dropdown)
 *
 * Loan car image categories and incident history are fetched via
 * loanCarIncidentApi — thunks here call that module, not bookingApi.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getBookingsApi,
    getBookingApi,
    createBookingApi,
    createServiceProfileBookingApi,
    createBookingFromMileageApi,
    updateBookingApi,
    cancelBookingApi,
    rescheduleBookingApi,
    updateBookingStatusApi,
    markVehicleReceivedApi,
    getBookingsByDateApi,
    getBookingsForWeekApi,
    getBookingsForMonthApi,
    getUpcomingBookingsApi,
    getBookingSummaryApi,
    getBookingServiceCategoriesApi,
} from '../../services/bookingApi';
import {
    getLoanCarImageCategoriesApi,
    getLoanCarIncidentsByBookingApi,
} from '../../services/loanCarIncidentApi';

// ============================================
// Helper Functions
// ============================================

const formatDate = (date) => {
    if (!date) return null;
    if (typeof date === 'string') return date;
    return date.toISOString().split('T')[0];
};

const formatTime = (time) => {
    if (!time) return null;
    if (typeof time === 'string') {
        return time.substring(0, 5);
    }
    return time;
};

const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
};

// ============================================
// Async Thunks - Fetch Operations
// ============================================

export const fetchBookings = createAsyncThunk(
    'bookings/fetchAll',
    async (_, { rejectWithValue }) => {
        try {
            return await getBookingsApi();
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch bookings');
        }
    }
);

export const fetchBooking = createAsyncThunk(
    'bookings/fetchOne',
    async (uuid, { rejectWithValue }) => {
        try {
            return await getBookingApi(uuid);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch booking');
        }
    }
);

export const fetchBookingsByDate = createAsyncThunk(
    'bookings/fetchByDate',
    async (date, { rejectWithValue }) => {
        try {
            const formattedDate = formatDate(date);
            return await getBookingsByDateApi(formattedDate);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch bookings for date');
        }
    }
);

export const fetchBookingsForWeek = createAsyncThunk(
    'bookings/fetchForWeek',
    async (weekStart, { rejectWithValue }) => {
        try {
            const formattedDate = formatDate(weekStart);
            return await getBookingsForWeekApi(formattedDate);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch bookings for week');
        }
    }
);

export const fetchBookingsForMonth = createAsyncThunk(
    'bookings/fetchForMonth',
    async ({ year, month }, { rejectWithValue }) => {
        try {
            return await getBookingsForMonthApi(year, month);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch bookings for month');
        }
    }
);

export const fetchUpcomingBookings = createAsyncThunk(
    'bookings/fetchUpcoming',
    async (_, { rejectWithValue }) => {
        try {
            return await getUpcomingBookingsApi();
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch upcoming bookings');
        }
    }
);

export const fetchBookingSummary = createAsyncThunk(
    'bookings/fetchSummary',
    async (_, { rejectWithValue }) => {
        try {
            return await getBookingSummaryApi();
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch booking summary');
        }
    }
);

// ============================================
// Async Thunks - Metadata
// ============================================

/**
 * Fetch BookingServiceCategory enum values for the Service Type dropdown.
 * Hits GET /bookings/meta/service-categories.
 * Dispatched once on BookingsPage mount; result cached in state.
 */
export const fetchBookingServiceCategories = createAsyncThunk(
    'bookings/fetchBookingServiceCategories',
    async (_, { rejectWithValue }) => {
        try {
            return await getBookingServiceCategoriesApi();
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch service categories');
        }
    }
);

/**
 * Fetch loan car image categories for the VehicleReceivedModal.
 * Hits GET /loan-car-incidents/image-categories (via loanCarIncidentApi).
 * Dispatched once on BookingsPage mount; result cached in state.
 * Returns [{ category, label, description, mandatory }]
 */
export const fetchLoanCarImageCategories = createAsyncThunk(
    'bookings/fetchLoanCarImageCategories',
    async (_, { rejectWithValue }) => {
        try {
            return await getLoanCarImageCategoriesApi();
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch loan car image categories');
        }
    }
);

/**
 * Fetch all loan car incidents for a booking.
 * Hits GET /loan-car-incidents/booking/{bookingUuid} (via loanCarIncidentApi).
 * Dispatched when the booking detail modal opens (if hasLoanCar is true).
 * Returns [{ incidentId, type, rego, odometerReading, insuranceTaken, notes, imagesByCategory, recordedAt }]
 */
export const fetchLoanCarIncidents = createAsyncThunk(
    'bookings/fetchLoanCarIncidents',
    async (bookingUuid, { rejectWithValue }) => {
        try {
            return await getLoanCarIncidentsByBookingApi(bookingUuid);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch loan car incidents');
        }
    }
);

// ============================================
// Async Thunks - CRUD Operations
// ============================================

/**
 * Create an ad-hoc (on-demand) booking.
 * Dispatched when the user completes the booking form without a service profile.
 * Hits POST /bookings.
 *
 * The payload is shaped by BookingFormModal.handleFinalSubmit. Notable:
 *   - serviceTypes is an array — each entry carries { type, durationMinutes, description }.
 *     A booking can have multiple service types (e.g. oil change + brake service).
 *   - estimatedDurationMinutes is the FE-computed sum of per-row durations,
 *     sent for backend convenience (avoids re-summing on read paths).
 * See bookingApi.createBookingApi for the full body shape.
 */
export const createBooking = createAsyncThunk(
    'bookings/create',
    async (data, { rejectWithValue }) => {
        try {
            return await createBookingApi(data);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create booking');
        }
    }
);

/**
 * Create a booking from a ServiceProfile.
 * Dispatched when the user selects a service profile in Step 2 and submits.
 * Hits POST /bookings/from-service-profile.
 *
 * The payload is shaped by BookingFormModal.handleFinalSubmit and contains:
 *   scheduledDate, scheduledTime, serviceProfileUuid, description, customer, vehicle.
 * serviceTypes and estimatedDurationMinutes are intentionally absent — the backend
 * derives them from the profile's work items.
 */
export const createServiceProfileBooking = createAsyncThunk(
    'bookings/createFromServiceProfile',
    async (data, { rejectWithValue }) => {
        try {
            return await createServiceProfileBookingApi(data);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create service profile booking');
        }
    }
);

export const createMileageBooking = createAsyncThunk(
    'bookings/createFromMileage',
    async (data, { rejectWithValue }) => {
        try {
            return await createBookingFromMileageApi(data);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create booking from the odometer reading');
        }
    }
);

export const updateBooking = createAsyncThunk(
    'bookings/update',
    async ({ uuid, data }, { rejectWithValue }) => {
        try {
            return await updateBookingApi(uuid, data);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update booking');
        }
    }
);

/**
 * Inline-edit variant of updateBooking, used by BookingDetailModal's
 * drop-off / pickup person tiles. Hits the same endpoint with the same body
 * shape, but its fulfilled handler does NOT close the view modal or null out
 * selectedBooking — it merges the updated booking into the bookings list AND
 * into selectedBooking, so the modal stays open and the inputs return to
 * display mode with the new values.
 *
 * Kept as a separate thunk (rather than a flag on updateBooking) so the
 * existing edit-modal flow's "submit closes the modal" behaviour is
 * untouched.
 */
export const updateBookingPersons = createAsyncThunk(
    'bookings/updatePersons',
    async ({ uuid, data }, { rejectWithValue }) => {
        try {
            return await updateBookingApi(uuid, data);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update booking');
        }
    }
);

export const cancelBooking = createAsyncThunk(
    'bookings/cancel',
    async (uuid, { rejectWithValue }) => {
        try {
            await cancelBookingApi(uuid);
            return uuid;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to cancel booking');
        }
    }
);

// ============================================
// Async Thunks - Reschedule (Drag-and-Drop)
// ============================================

export const rescheduleBooking = createAsyncThunk(
    'bookings/reschedule',
    async ({ uuid, newDate, newTime }, { rejectWithValue }) => {
        try {
            const formattedDate = formatDate(newDate);
            const formattedTime = formatTime(newTime);
            return await rescheduleBookingApi(uuid, formattedDate, formattedTime);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to reschedule booking');
        }
    }
);

// ============================================
// Async Thunks - Status Update
// ============================================

/**
 * Generic status update — for all statuses EXCEPT VEHICLE_RECEIVED.
 * VEHICLE_RECEIVED must go through markVehicleReceived so loan car
 * handover details can be collected via the VehicleReceivedModal.
 */
export const updateBookingStatus = createAsyncThunk(
    'bookings/updateStatus',
    async ({ uuid, status }, { rejectWithValue }) => {
        try {
            return await updateBookingStatusApi(uuid, status);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update booking status');
        }
    }
);

/**
 * Transition a booking to VEHICLE_RECEIVED.
 * Hits PATCH /bookings/{uuid}/vehicle-received.
 *
 * Payload:
 *   { uuid, loanCarDetails }
 *
 * loanCarDetails is:
 *   - { odometerReading, insuranceTaken, notes, imagesByCategory } when the booking
 *     has a loan car allocation. imagesByCategory contains s3Keys from
 *     loanCarIncidentApi.uploadLoanCarPhotoApi.
 *   - null when the booking has no loan car.
 *
 * The backend enforces the mandatory check — if a loan car exists and
 * loanCarDetails is null the request will be rejected with 400.
 *
 * After a successful response, this thunk chains a fetchLoanCarIncidents
 * dispatch so the BookingDetailModal's "Loan Car Handover" panel re-renders
 * with the newly-recorded OUT incident (and its photos) without the user
 * having to close and reopen the modal. The chain only fires when a loan
 * car is actually involved (loanCarDetails != null) — there's nothing to
 * fetch otherwise.
 */
export const markVehicleReceived = createAsyncThunk(
    'bookings/markVehicleReceived',
    async ({ uuid, loanCarDetails, customerEmail = null, customerName = null }, { dispatch, rejectWithValue }) => {
        try {
            const response = await markVehicleReceivedApi(uuid, loanCarDetails, customerEmail, customerName);
            // Refresh the incidents list so the handover panel reflects the new OUT
            // record. Fire-and-forget — we don't await it so the modal close path
            // (driven by the fulfilled reducer below) isn't gated on a second call.
            if (loanCarDetails != null) {
                dispatch(fetchLoanCarIncidents(uuid));
            }
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to mark vehicle as received'
            );
        }
    }
);

// ============================================
// Initial State
// ============================================

const initialState = {
    // Bookings data
    bookings: [],
    selectedBooking: null,

    // Loading states
    bookingsLoading: false,
    bookingsError: null,

    // Calendar state
    viewMode: 'week', // 'day' | 'week' | 'month'
    currentDate: new Date().toISOString(),

    // Summary
    summary: null,
    summaryLoading: false,

    // UI State - Modals
    showCreateModal: false,
    showEditModal: false,
    showViewModal: false,
    showCancelConfirm: false,
    // VehicleReceivedModal: shown when clicking VEHICLE_RECEIVED on a booking
    // that has an active loan car allocation.
    showVehicleReceivedModal: false,

    // Modal context - for creating bookings from calendar clicks
    modalContext: null, // { date, time } - pre-fill values when creating from calendar

    // Drag-and-drop state
    draggingBooking: null,
    dropTarget: null,

    // Operation states
    operationLoading: false,
    operationError: null,
    operationSuccess: null,

    // Enum metadata (populated once on mount from /meta endpoint)
    bookingServiceCategories: [],  // [{ value, label, description }]
    metaLoading: false,
    loanCarImageCategories: [],    // [{ category, label, description, mandatory }]
    loanCarImageCategoriesLoading: false,

    // Loan car incidents for the selected booking (loaded on detail modal open)
    loanCarIncidents: [],          // [{ incidentId, type, rego, odometerReading, ... }]
    loanCarIncidentsLoading: false,
    loanCarIncidentsError: null,
};

// ============================================
// Slice
// ============================================

const bookingSlice = createSlice({
    name: 'bookings',
    initialState,
    reducers: {
        // View mode
        setViewMode: (state, action) => {
            state.viewMode = action.payload;
        },

        // Navigation
        setCurrentDate: (state, action) => {
            state.currentDate = action.payload;
        },
        navigateToToday: (state) => {
            state.currentDate = new Date().toISOString();
        },
        navigateNext: (state) => {
            const current = new Date(state.currentDate);
            if (state.viewMode === 'day') {
                current.setDate(current.getDate() + 1);
            } else if (state.viewMode === 'week') {
                current.setDate(current.getDate() + 7);
            } else {
                current.setMonth(current.getMonth() + 1);
            }
            state.currentDate = current.toISOString();
        },
        navigatePrevious: (state) => {
            const current = new Date(state.currentDate);
            if (state.viewMode === 'day') {
                current.setDate(current.getDate() - 1);
            } else if (state.viewMode === 'week') {
                current.setDate(current.getDate() - 7);
            } else {
                current.setMonth(current.getMonth() - 1);
            }
            state.currentDate = current.toISOString();
        },

        // Modal controls
        openCreateModal: (state, action) => {
            state.showCreateModal = true;
            state.modalContext = action.payload || null; // { date, time }
            state.operationError = null;
            state.operationSuccess = null;
        },
        closeCreateModal: (state) => {
            state.showCreateModal = false;
            state.modalContext = null;
        },
        openEditModal: (state, action) => {
            state.showEditModal = true;
            state.selectedBooking = action.payload;
            state.operationError = null;
            state.operationSuccess = null;
        },
        closeEditModal: (state) => {
            state.showEditModal = false;
            state.selectedBooking = null;
        },
        openViewModal: (state, action) => {
            state.showViewModal = true;
            state.selectedBooking = action.payload;
            // Clear stale incident data from any previously-viewed booking
            state.loanCarIncidents = [];
            state.loanCarIncidentsError = null;
        },
        closeViewModal: (state) => {
            state.showViewModal = false;
            state.selectedBooking = null;
        },
        openCancelConfirm: (state, action) => {
            state.showCancelConfirm = true;
            state.selectedBooking = action.payload;
        },
        closeCancelConfirm: (state) => {
            state.showCancelConfirm = false;
            state.selectedBooking = null;
        },

        /**
         * Open the VehicleReceivedModal.
         * Called when staff click "VEHICLE RECEIVED" on a booking that has
         * an active loan car allocation. The booking is stored in selectedBooking
         * so the modal can display booking details.
         */
        openVehicleReceivedModal: (state, action) => {
            state.showVehicleReceivedModal = true;
            state.selectedBooking = action.payload;
            state.operationError = null;
        },
        closeVehicleReceivedModal: (state) => {
            state.showVehicleReceivedModal = false;
            // Keep selectedBooking — the view modal may still be open underneath
        },

        // Drag-and-drop
        setDraggingBooking: (state, action) => {
            state.draggingBooking = action.payload;
        },
        setDropTarget: (state, action) => {
            state.dropTarget = action.payload;
        },
        clearDragState: (state) => {
            state.draggingBooking = null;
            state.dropTarget = null;
        },

        // Clear operation status
        clearOperationStatus: (state) => {
            state.operationError = null;
            state.operationSuccess = null;
        },

        // Select booking
        selectBooking: (state, action) => {
            state.selectedBooking = action.payload;
        },
        clearSelectedBooking: (state) => {
            state.selectedBooking = null;
        },
    },
    extraReducers: (builder) => {
        // ==================== Fetch All Bookings ====================
        builder
            .addCase(fetchBookings.pending, (state) => {
                state.bookingsLoading = true;
                state.bookingsError = null;
            })
            .addCase(fetchBookings.fulfilled, (state, action) => {
                state.bookingsLoading = false;
                state.bookings = action.payload;
            })
            .addCase(fetchBookings.rejected, (state, action) => {
                state.bookingsLoading = false;
                state.bookingsError = action.payload;
            })

            // ==================== Fetch By Date ====================
            .addCase(fetchBookingsByDate.pending, (state) => {
                state.bookingsLoading = true;
                state.bookingsError = null;
            })
            .addCase(fetchBookingsByDate.fulfilled, (state, action) => {
                state.bookingsLoading = false;
                state.bookings = action.payload;
            })
            .addCase(fetchBookingsByDate.rejected, (state, action) => {
                state.bookingsLoading = false;
                state.bookingsError = action.payload;
            })

            // ==================== Fetch For Week ====================
            .addCase(fetchBookingsForWeek.pending, (state) => {
                state.bookingsLoading = true;
                state.bookingsError = null;
            })
            .addCase(fetchBookingsForWeek.fulfilled, (state, action) => {
                state.bookingsLoading = false;
                state.bookings = action.payload;
            })
            .addCase(fetchBookingsForWeek.rejected, (state, action) => {
                state.bookingsLoading = false;
                state.bookingsError = action.payload;
            })

            // ==================== Fetch For Month ====================
            .addCase(fetchBookingsForMonth.pending, (state) => {
                state.bookingsLoading = true;
                state.bookingsError = null;
            })
            .addCase(fetchBookingsForMonth.fulfilled, (state, action) => {
                state.bookingsLoading = false;
                state.bookings = action.payload;
            })
            .addCase(fetchBookingsForMonth.rejected, (state, action) => {
                state.bookingsLoading = false;
                state.bookingsError = action.payload;
            })

            // ==================== Fetch Summary ====================
            .addCase(fetchBookingSummary.pending, (state) => {
                state.summaryLoading = true;
            })
            .addCase(fetchBookingSummary.fulfilled, (state, action) => {
                state.summaryLoading = false;
                state.summary = action.payload;
            })
            .addCase(fetchBookingSummary.rejected, (state) => {
                state.summaryLoading = false;
            })

            // ==================== Fetch Booking Service Categories ====================
            .addCase(fetchBookingServiceCategories.pending, (state) => {
                state.metaLoading = true;
            })
            .addCase(fetchBookingServiceCategories.fulfilled, (state, action) => {
                state.metaLoading = false;
                state.bookingServiceCategories = action.payload;
            })
            .addCase(fetchBookingServiceCategories.rejected, (state) => {
                state.metaLoading = false;
                // Leave bookingServiceCategories as [] — form will show a disabled placeholder
            })

            // ==================== Fetch Loan Car Image Categories ====================
            .addCase(fetchLoanCarImageCategories.pending, (state) => {
                state.loanCarImageCategoriesLoading = true;
            })
            .addCase(fetchLoanCarImageCategories.fulfilled, (state, action) => {
                state.loanCarImageCategoriesLoading = false;
                state.loanCarImageCategories = action.payload;
            })
            .addCase(fetchLoanCarImageCategories.rejected, (state) => {
                state.loanCarImageCategoriesLoading = false;
                // Leave as [] — modal falls back to hardcoded categories
            })

            // ==================== Fetch Loan Car Incidents ====================
            .addCase(fetchLoanCarIncidents.pending, (state) => {
                state.loanCarIncidentsLoading = true;
                state.loanCarIncidentsError = null;
            })
            .addCase(fetchLoanCarIncidents.fulfilled, (state, action) => {
                state.loanCarIncidentsLoading = false;
                state.loanCarIncidents = action.payload;
            })
            .addCase(fetchLoanCarIncidents.rejected, (state, action) => {
                state.loanCarIncidentsLoading = false;
                state.loanCarIncidentsError = action.payload;
                state.loanCarIncidents = [];
            })

            // ==================== Create Booking (Ad-hoc / On Demand) ====================
            .addCase(createBooking.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(createBooking.fulfilled, (state, action) => {
                state.operationLoading = false;
                state.bookings.push(action.payload);
                state.showCreateModal = false;
                state.modalContext = null;
                state.operationSuccess = 'Booking created successfully';
            })
            .addCase(createBooking.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Create Booking (Service Profile) ====================
            .addCase(createServiceProfileBooking.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(createServiceProfileBooking.fulfilled, (state, action) => {
                state.operationLoading = false;
                state.bookings.push(action.payload);
                state.showCreateModal = false;
                state.modalContext = null;
                state.operationSuccess = 'Booking created successfully';
            })
            .addCase(createServiceProfileBooking.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Create Booking From Mileage ====================
            // Same three transitions as the profile route: from the list's point of view
            // a booking is a booking, and how its work was decided changes nothing here.
            .addCase(createMileageBooking.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(createMileageBooking.fulfilled, (state, action) => {
                state.operationLoading = false;
                state.bookings.push(action.payload);
                state.showCreateModal = false;
                state.modalContext = null;
                state.operationSuccess = 'Booking created successfully';
            })
            .addCase(createMileageBooking.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Update Booking ====================
            .addCase(updateBooking.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(updateBooking.fulfilled, (state, action) => {
                state.operationLoading = false;
                const index = state.bookings.findIndex(b => b.uuid === action.payload.uuid);
                if (index !== -1) {
                    state.bookings[index] = action.payload;
                }
                state.showEditModal = false;
                state.selectedBooking = null;
                state.operationSuccess = 'Booking updated successfully';
            })
            .addCase(updateBooking.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Update Booking — Inline Persons ====================
            // Same endpoint and payload shape as updateBooking, but the
            // fulfilled handler keeps the view modal open: selectedBooking is
            // refreshed in place rather than cleared, and showEditModal isn't
            // touched. Used by the drop-off / pickup person inline tiles.
            .addCase(updateBookingPersons.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(updateBookingPersons.fulfilled, (state, action) => {
                state.operationLoading = false;
                const updated = action.payload;
                const index = state.bookings.findIndex(b => b.uuid === updated.uuid);
                if (index !== -1) {
                    state.bookings[index] = updated;
                }
                // Keep the view modal open — refresh selectedBooking so the
                // tiles re-render with the new values immediately.
                if (state.selectedBooking && state.selectedBooking.uuid === updated.uuid) {
                    state.selectedBooking = updated;
                }
                state.operationSuccess = 'Booking updated successfully';
            })
            .addCase(updateBookingPersons.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Cancel Booking ====================
            .addCase(cancelBooking.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(cancelBooking.fulfilled, (state, action) => {
                state.operationLoading = false;
                state.bookings = state.bookings.filter(b => b.uuid !== action.payload);
                state.showCancelConfirm = false;
                state.selectedBooking = null;
                state.operationSuccess = 'Booking cancelled successfully';
            })
            .addCase(cancelBooking.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Reschedule Booking ====================
            .addCase(rescheduleBooking.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(rescheduleBooking.fulfilled, (state, action) => {
                state.operationLoading = false;
                const index = state.bookings.findIndex(b => b.uuid === action.payload.uuid);
                if (index !== -1) {
                    state.bookings[index] = action.payload;
                }
                state.draggingBooking = null;
                state.dropTarget = null;
                state.operationSuccess = 'Booking rescheduled successfully';
            })
            .addCase(rescheduleBooking.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
                state.draggingBooking = null;
                state.dropTarget = null;
            })

            // ==================== Update Status (generic) ====================
            .addCase(updateBookingStatus.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(updateBookingStatus.fulfilled, (state, action) => {
                state.operationLoading = false;
                const index = state.bookings.findIndex(b => b.uuid === action.payload.uuid);
                if (index !== -1) {
                    state.bookings[index] = action.payload;
                }
                if (state.selectedBooking?.uuid === action.payload.uuid) {
                    state.selectedBooking = action.payload;
                }
                state.operationSuccess = 'Status updated successfully';
            })
            .addCase(updateBookingStatus.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Mark Vehicle Received ====================
            .addCase(markVehicleReceived.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(markVehicleReceived.fulfilled, (state, action) => {
                state.operationLoading = false;
                const index = state.bookings.findIndex(b => b.uuid === action.payload.uuid);
                if (index !== -1) {
                    state.bookings[index] = action.payload;
                }
                if (state.selectedBooking?.uuid === action.payload.uuid) {
                    state.selectedBooking = action.payload;
                }
                state.showVehicleReceivedModal = false;
                state.operationSuccess = 'Vehicle received — loan car handover recorded';
            })
            .addCase(markVehicleReceived.rejected, (state, action) => {
                state.operationLoading = false;
                // Keep the modal open so the user can correct the error
                state.operationError = action.payload;
            });
    },
});

// ============================================
// Selectors
// ============================================

export const selectBookingsByDate = (state, date) => {
    const dateStr = formatDate(date);
    return state.bookings.bookings.filter(b => b.scheduledDate === dateStr);
};

export const selectBookingsGroupedByDate = (state) => {
    const grouped = {};
    state.bookings.bookings.forEach(booking => {
        const date = booking.scheduledDate;
        if (!grouped[date]) {
            grouped[date] = [];
        }
        grouped[date].push(booking);
    });
    return grouped;
};

export const selectCurrentWeekStart = (state) => {
    return getWeekStart(new Date(state.bookings.currentDate));
};

// ============================================
// Exports
// ============================================

export const {
    setViewMode,
    setCurrentDate,
    navigateToToday,
    navigateNext,
    navigatePrevious,
    openCreateModal,
    closeCreateModal,
    openEditModal,
    closeEditModal,
    openViewModal,
    closeViewModal,
    openCancelConfirm,
    closeCancelConfirm,
    openVehicleReceivedModal,
    closeVehicleReceivedModal,
    setDraggingBooking,
    setDropTarget,
    clearDragState,
    clearOperationStatus,
    selectBooking,
    clearSelectedBooking,
} = bookingSlice.actions;

export default bookingSlice.reducer;